import * as React from "react";
import { supabase } from "../logic/client";
import {
  Box,
  Typography,
  Button,
  Stack,
  Paper,
  LinearProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { useWorkshopBlocker, FLOW } from "../logic/useWorkshopBlocker";
import { useI18n } from "../i18n/I18nProvider";


export default function EmployeeScreen(props: { handlePageChange: (page: string) => void}) {
  const { handlePageChange } = props;
  const { lang } = useI18n();
  const blocker = useWorkshopBlocker();
  const isDE = lang === "de";
  const [notice, setNotice] = React.useState<string | null>(null);

  const L = React.useCallback(
    (en: string, de: string) => (lang === "de" ? de : en),
    [lang]
  );

  const [score, setScore] = React.useState<number>(0);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [uiError, setUiError] = React.useState<string | null>(null);

  const myIndex = FLOW.indexOf("EmployeeScreen");
  const blockerIndex = FLOW.indexOf(blocker);
  const canContinue = blockerIndex > myIndex;
  
  function handleContinue() {
    if (canContinue) {
      handlePageChange(FLOW[myIndex + 1]);
      return;
    }
    setNotice("ENG / DE");
  }

  React.useEffect(() => {
    let mounted = true;

    async function loadScore() {
      setLoading(true);
      setUiError(null);

      try {
        const { data: userData, error: userErr } =
          await supabase.auth.getUser();
        if (userErr) throw userErr;
        if (!userData?.user)
          throw new Error(L("Not authenticated.", "Nicht authentifiziert."));

        const authUserId = userData.user.id;

        const { data: empRow, error: empErr } = await supabase
          .from("employees")
          .select("id")
          .eq("auth_user_id", authUserId)
          .maybeSingle();

        if (empErr) throw empErr;
        if (!empRow?.id)
          throw new Error(
            L(
              "Employee record not found for this account.",
              "Kein Mitarbeiterdatensatz für dieses Konto gefunden."
            )
          );

        const { data: scoreRow, error: scoreErr } = await supabase
          .from("employee_scores")
          .select("kontrol_score")
          .eq("employee_id", empRow.id)
          .maybeSingle();

        if (scoreErr) throw scoreErr;

        const nextScore =
          typeof scoreRow?.kontrol_score === "number"
            ? scoreRow.kontrol_score
            : Number(scoreRow?.kontrol_score ?? 0) || 0;

        if (!mounted) return;
        setScore(nextScore);
      } catch (e: any) {
        if (!mounted) return;
        setUiError(
          e?.message ??
            L(
              "Failed to load LAB score.",
              "LAB-Score konnte nicht geladen werden."
            )
        );
        setScore(0);
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    }

    loadScore();

    return () => {
      mounted = false;
    };
  }, [L]);

  const meta = scoreLabel(score, lang);
  const progress = (score / 5) * 100;

  // Meaning text
  const scoreMeaning =
    score <= 1
      ? L(
          "Important readiness gaps are likely present.",
          "Es gibt vermutlich wichtige Lücken in der Vorbereitung."
        )
      : score <= 3
      ? L(
          "You have some coverage, but key gaps remain.",
          "Es gibt schon einiges, aber zentrale Lücken bleiben."
        )
      : L(
          "Strong baseline readiness with room to refine.",
          "Gute Basis - mit Potenzial zur Feinjustierung."
        );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        px: { xs: 2, sm: 3 },
        py: { xs: 3, sm: 6 },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `
          radial-gradient(circle at 20% 20%, rgba(251, 2, 2, 0.08), transparent 40%),
          radial-gradient(circle at 80% 30%, rgba(255, 0, 0, 0.06), transparent 40%),
          #111010
        `,
        backgroundImage: `
          linear-gradient(
            rgba(8, 8, 8, 0.78),
            rgba(8, 8, 8, 0.82)
          ),
          url("/scenarioBgs/availability.png")
        `,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
      }}
    >
      <Paper
        sx={{
          width: "min(920px, 92vw)",
          borderRadius: 5,
          p: { xs: 3, sm: 5, md: 6 },
          border: "1px solid rgba(255,255,255,0.14)",
          background:
            "linear-gradient(180deg, rgba(49, 32, 32, 0.95), rgba(34, 21, 21, 0.94))",
          boxShadow: "0 24px 65px rgba(0,0,0,0.45)",
        }}
      >
        <Stack spacing={5}>
          {/* HEADER */}
          <Stack spacing={2} alignItems="center" textAlign="center">
            <Typography
              sx={{
                color: "#fff",
                fontWeight: 900,
                fontSize: { xs: "1.6rem", sm: "2rem" },
              }}
            >
              {L("Your LAB Score", "Dein LAB-Score")}
            </Typography>

            <Typography sx={{ color: "rgba(255,255,255,0.7)", maxWidth: 520 }}>
              {L(
                "Snapshot of your current operational availability.",
                "Momentaufnahme deiner aktuellen Einsatzverfügbarkeit."
              )}
            </Typography>
          </Stack>

          {/* SCORE */}
          <Stack spacing={3} alignItems="center" textAlign="center">
            <Stack direction="row" alignItems="baseline" spacing={1}>
              <Typography
                sx={{
                  fontSize: 72,
                  fontWeight: 1000,
                  color: meta.color,
                  opacity: loading ? 0.6 : 1,
                }}
              >
                {loading ? "…" : score}
              </Typography>

              <Typography
                sx={{
                  color: "rgba(255,255,255,0.5)",
                  fontWeight: 700,
                  fontSize: 20,
                }}
              >
                / 100
              </Typography>
            </Stack>

            <Typography
              sx={{
                fontWeight: 800,
                color: meta.color,
                fontSize: "1.1rem",
              }}
            >
              {meta.label}
            </Typography>

            {/* PROGRESS */}
            <Box sx={{ width: "100%", maxWidth: 420 }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 10,
                  borderRadius: 99,
                  backgroundColor: "rgba(255,255,255,0.1)",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 99,
                    background:
                      "linear-gradient(90deg, #b50d20, #d6293e)",
                  },
                }}
              />
            </Box>

            {/* MEANING PANEL */}
            <Box
              sx={{
                mt: 1,
                px: 3,
                py: 2,
                borderRadius: 3,
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.1)",
                maxWidth: 520,
              }}
            >
              <Typography
                sx={{
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "0.95rem",
                }}
              >
                {scoreMeaning}
              </Typography>
            </Box>

            {uiError && (
              <Typography sx={{ color: "#ff8a8a", fontWeight: 700 }}>
                {uiError}
              </Typography>
            )}
          </Stack>

          {/* CTA */}
          <Stack spacing={2} alignItems="center">
            {/*<Button
              variant="contained"
              onClick={() => handlePageChange("JourneyPillars")}
              endIcon={<ArrowForwardIcon />}
              sx={{
                background: "linear-gradient(90deg, #b50d20, #d6293e)",
                px: 4,
                py: 1.4,
                borderRadius: 999,
                fontWeight: 800,
                textTransform: "none",
                "&:hover": { opacity: 0.9 },
              }}
            >
              {L("Back to Overview", "Zurück zur Übersicht")}
            </Button>*/}

            <Button
              variant="outlined"
              onClick={() => handlePageChange("AvailabilityCheck")}
              sx={{
                borderColor: "rgba(255,255,255,0.35)",
                color: "#fff",
                px: 3,
                py: 1.2,
                borderRadius: 999,
                textTransform: "none",
                fontWeight: 700,
                "&:hover": {
                  borderColor: "#fff",
                  background: "rgba(255,255,255,0.05)",
                },
              }}
            >
              {L("Retake availability check", "Verfügbarkeits-Check wiederholen")}
            </Button>
          </Stack>

            <Button
              variant="contained"
              onClick={handleContinue}
              sx={{
                mt: 1,
                borderRadius: 999,
                textTransform: "none",
                fontWeight: 800,
                px: 3,
                bgcolor: canContinue ? "#ffffff" : "rgba(255,255,255,0.35)",
                color: canContinue ? "#111827" : "rgba(255,255,255,0.8)",
                "&:hover": {
                  bgcolor: canContinue ? "#f3f4f6" : "rgba(255,255,255,0.35)",
                },
              }}
            >
              {isDE ? "Weiter" : "Continue"}
            </Button>
        </Stack>
      </Paper>
      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={5000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setNotice(null)} severity="info" sx={{ width: "100%", fontWeight: 700 }}>
          {isDE ? "Die Workshop-Leitung wird diesen Schritt freigeben, oder bitte beantworte die Frage vollständig und sende sie zuerst ab." : "The workshop holder will enable this step, or please complete and submit your answer first."}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function scoreLabel(
  score: number,
  lang: "en" | "de"
): { label: string; color: string } {
  if (score >= 4)
    return {
      label: lang === "de" ? "Hohe Verfügbarkeit" : "High readiness",
      color: "#22c55e",
    };
  if (score >= 2)
    return {
      label: lang === "de" ? "Mittlere Verfügbarkeit" : "Moderate readiness",
      color: "#f59e0b",
    };
  return {
    label: lang === "de" ? "Niedrige Verfügbarkeit" : "Low readiness",
    color: "#ef4444",
  };
}